import * as yup from 'yup';
import { DocumentNode } from 'graphql';
import { FocusEvent } from 'react';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import {
  AddTicketCategoryMutation as AddTicketCategory, GetTicketsQuery as GetTickets, SportType,
} from '__generated__/graphql';

import { DateTimeForm } from '../../../Common/DateRangeForm';
import { getServerErrors } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import AddTicketCategoryMutation from './AddTicketCategoryMutation';
import GetTicketsQuery from './GetTicketsQuery';
import UI from '../../../../common/UI';
import useGroupedSportTypes from '../../useGroupedSportTypes';
import useMutation from '../../../../api/useMutation';
import useValidation from '../../../../common/useValidation';

type Event = GetTickets['project']['event'];

const addTicketCategorySchema = yup.object().shape({
  title: yup.string().required(),
  sport_type: yup.string().required(),
});

export interface AddTicketCategoryFormProps {
  event: Event;
  onSuccess?: (ticketCategory: AddTicketCategory['addTicketCategory']) => void;
  refetchQueries?: DocumentNode[];
}

const AddTicketCategoryForm = ({
  event, onSuccess, refetchQueries = [GetTicketsQuery],
}: AddTicketCategoryFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const form = useForm({
    values: {
      title: '',
      // Re-use recent values.
      sport_type: event.ticket_categories[event.ticket_categories.length - 1]?.sport_type || '' as SportType,
      start_date: event.ticket_categories[event.ticket_categories.length - 1]?.start_date || event.start_date,
    },
  });

  const [addTicketCategory, { error, loading }] = useMutation(AddTicketCategoryMutation, {
    variables: {
      input: {
        event: {
          id: event.id,
        },
        ...form.values,
      },
    },
    onCompleted: ({ addTicketCategory: ticketCategory }) => {
      notifier.success(t('notifications.ticket_category_added'));
      onSuccess?.(ticketCategory);
    },
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const { popularSportTypes, otherSportTypes } = useGroupedSportTypes();

  const { valid, errors } = useValidation({
    schema: addTicketCategorySchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const handleTitleBlur = (focusEvent: FocusEvent<HTMLInputElement>) => {
    if (!focusEvent.target.value) {
      form.set('title', event.title);
      focusEvent.target.value = event.title;
    }
  };

  return (
    <UI.Form onSubmit={() => addTicketCategory()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>
            {t('add_a_ticket_category')}
          </UI.H4>

          <UI.Paragraph sc={{ mb: 0 }}>
            {t('add_ticket_category_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <UI.InputGroup sc={{ valid: !errors.title, touched: form.getTouched('title') }}>
          <UI.InputLabel htmlFor="ticket_category_title">
            {t('ticket_category_title')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.DebouncedInput
            {...form.text('title', { onBlur: (value, event) => {
              handleTitleBlur(event as FocusEvent<HTMLInputElement>);
            } })}
            maxLength={50}
            id="ticket_category_title"
            placeholder={event.title}
            autoFocus
          />
          <UI.ErrorMessages attribute={t('ticket_category_title')} errors={errors.title} />
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors.sport_type, touched: form.touched.sport_type }}>
          <UI.InputLabel htmlFor="sport_type">
            {t('sport_type')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.Select id="sport_type" {...form.select('sport_type')}>
            {!form.values.sport_type && <option value=""> </option>}
            <optgroup label={t('popular')}>
              {popularSportTypes.map((category: string, index: number) => (
                <option {...form.option(category)} key={index}>
                  {category && t(`common:sport_types.${category}`)}
                </option>
              ))}
            </optgroup>
            <optgroup label={t('other')}>
              {otherSportTypes.map((category: string, index: number) => (
                <option {...form.option(category)} key={index}>
                  {category && t(`common:sport_types.${category}`)}
                </option>
              ))}
            </optgroup>
          </UI.Select>
          <UI.ErrorMessages attribute={t('sport_type')} errors={errors.sport_type} />
        </UI.InputGroup>

        <DateTimeForm
          form={form}
          errors={errors}
          keys={{ date: 'start_date', time: 'start_time' }}
          labels={{ date: t('event_date'), time: t('time') }}
          require={false}
        />

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('add_ticket_category')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default AddTicketCategoryForm;
